<!-- eslint-disable vue/no-v-html -->
<template>
    <Teleport to="body">
        <div
            class="cookie-banner print:hidden"
            :class="{ active: store.cookiesBannerActive }"
            data-nosnippet
        >
            <div class="cookie-title">
                {{ $t("cookies.banner-title") }}
            </div>
            <div class="cookie-main-text">
                <p>{{ $t("cookies.banner-main-text") }}</p>
            </div>
            <div class="cookie-btns">
                <button
                    type="button"
                    class="cookie-btn"
                    @click="showSettings"
                >
                    {{ $t("cookies.settings") }}
                </button>
                <button
                    type="button"
                    class="cookie-btn"
                    @click="allowNecessaryPermissions"
                >
                    {{ $t("cookies.allow-necessary-btn") }}
                </button>
                <button
                    class="cookie-btn allow"
                    @click="allowAll"
                >
                    {{ $t("cookies.allow-all-btn") }}
                </button>
            </div>
        </div>
    </Teleport>
    <Teleport to="body">
        <div
            class="cookie-modal print:hidden"
            :class="{ active: store.cookiesSettingsModalActive }"
            data-nosnippet
        >
            <div class="cookie-modal-div">
                <div class="cookie-title">
                    {{ $t("cookies.modal-title") }}
                </div>
                <div class="cookie-main-text">
                    <p>
                        {{ $t("cookies.modal-main-text") }}
                    </p>
                </div>

                <div
                    v-for="(item, id) in store.cookiesPermissions"
                    :key="id"
                    class="cookie-settings-item"
                >
                    <div class="cookie-settings-item-div">
                        <input
                            type="checkbox"
                            :disabled="id === 'technical'"
                            :checked="item"
                            @change="handleCheckboxChange($event, id)"
                        />
                        <span>
                            {{ $t(`cookies.${id}.title`) }}
                        </span>
                    </div>
                    <p v-html="$t(`cookies.${id}.text`)" />
                </div>

                <div class="cookie-btns">
                    <button
                        class="cookie-btn"
                        @click="allowChosenPermissions"
                    >
                        {{ $t("cookies.allow-chosen-btn") }}
                    </button>
                    <button
                        class="cookie-btn"
                        @click="allowNecessaryPermissions"
                    >
                        {{ $t("cookies.allow-necessary-btn") }}
                    </button>
                    <button
                        class="cookie-btn allow"
                        @click="allowAll"
                    >
                        {{ $t("cookies.allow-all-btn") }}
                    </button>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
const store = useMainStore()
const { initialize, disableAnalytics, enableAnalytics } = useGtag()

const isGtagInitialized = ref(false)

onMounted(() => {
    activateBanner()
})

function activateBanner() {
    Object.keys(store.cookiesPermissions).forEach(key => {
        const cookie = getCookie(key)
        if (cookie === "true") {
            store.cookiesPermissions[key as CookiesType] = true
        }
        if (key === "analytical") {
            if (cookie === "true") {
                allowAnalytics()
            } else {
                disableAnalytics()
            }
        }
    })

    if (Object.values(store.cookiesPermissions).some(el => el === false)) {
        setTimeout(() => {
            store.cookiesBannerActive = true
        }, 500)
    }
}

function showSettings() {
    store.cookiesPermissions["technical"] = true
    store.cookiesBannerActive = false
    store.cookiesSettingsModalActive = true
}

function allowAll() {
    Object.keys(store.cookiesPermissions).forEach(key => {
        store.cookiesPermissions[key as CookiesType] = true
        setCookie(key, true)
    })
    allowAnalytics()
    store.cookiesBannerActive = false
    store.cookiesSettingsModalActive = false
}

function allowNecessaryPermissions() {
    Object.keys(store.cookiesPermissions).forEach(key => {
        if (key === "technical") {
            store.cookiesPermissions[key] = true
            setCookie(key, true)
        } else {
            store.cookiesPermissions[key as CookiesType] = false
            setCookie(key, false)
        }
    })
    disableAnalytics()
    store.cookiesBannerActive = false
    store.cookiesSettingsModalActive = false
}

function allowChosenPermissions() {
    if (store.cookiesPermissions["analytical"]) {
        allowAnalytics()
    } else {
        disableAnalytics()
    }
    Object.keys(store.cookiesPermissions).forEach(key => {
        setCookie(key, store.cookiesPermissions[key as CookiesType])
    })
    store.cookiesBannerActive = false
    store.cookiesSettingsModalActive = false
}

function handleCheckboxChange(e: Event, id: CookiesType) {
    const el = e.target as HTMLInputElement
    store.cookiesPermissions[id] = el.checked
}

/* function checkCookie (name: string, defaultValue?: string) {
    const cookieExists = document.cookie.indexOf(name) > -1 ? true : false
    if (cookieExists) {
        return getCookie(name)
    }
    return defaultValue
} */

function getCookie(name: string) {
    const cookie: { [k: string]: string } = {}
    document.cookie.split(";").forEach(function (el) {
        const [k, v] = el.split("=")
        if (k && v) {
            cookie[k.trim()] = v
        }
    })
    return cookie["portal-uap" + "_" + name]
}

function setCookie(name: string, value: string | boolean) {
    //cookies set for 30 days
    document.cookie = `${"portal-uap"}_${name}=${value}; max-age=2592000`
}

function allowAnalytics() {
    if (!isGtagInitialized.value) {
        initialize()
        isGtagInitialized.value = true
    } else {
        enableAnalytics()
    }
}
</script>

<style lang="scss">
.cookie-banner {
    @apply fixed bottom-[5vh] left-[5vw] max-h-[90vh] w-[90vw] overflow-y-auto px-6 py-4 sm:px-8;
    @apply z-50 sm:bottom-0 sm:left-0 sm:max-h-none sm:w-full;
    @apply translate-y-[150%] rounded-md border-t bg-white shadow-lg duration-200 ease-in sm:translate-y-full sm:rounded-none;
    @apply overflow-y-auto;
    &.active {
        @apply translate-y-0 ease-out;
    }
    .cookie-main-text p {
        @apply my-4 text-sm sm:my-8 sm:text-base;
    }
}

.cookie-title {
    @apply my-4 font-promedium text-xl;
}

.cookie-btns {
    @apply mt-4 flex justify-center gap-2 sm:justify-end sm:gap-3;
    .cookie-btn {
        @apply relative block w-fit border-0 border-black-text bg-white px-2 py-1.5 text-center text-black-text sm:px-4;
        @apply cursor-pointer font-proregular text-xs hover:bg-black-text hover:text-white sm:text-base;
        &.allow {
            @apply bg-black-text text-white;
        }
    }
}

.cookie-modal {
    @apply fixed inset-0 flex items-center justify-center;
    @apply invisible z-50 bg-black/30 font-light opacity-0 duration-200 ease-in;
    .cookie-modal-div {
        @apply h-fit max-h-[90%] w-4/5 max-w-[800px] border border-black-text bg-white p-4 shadow-lg sm:p-8;
        @apply scale-75 text-sm duration-100 ease-in sm:text-base;
    }
    &.active {
        @apply visible opacity-100 ease-out;
        .cookie-modal-div {
            @apply scale-100 duration-100 ease-out;
        }
    }
    .cookie-settings-item {
        @apply my-4 pl-6;
        .cookie-settings-item-div {
            @apply flex items-center gap-3;
            span {
                @apply font-promedium;
            }
        }
        p {
            @apply text-sm;
        }
    }
}
</style>
